import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Edit, X } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", authors: "", journal: "", year: 2024, summary: "", url: "" };

export default function ResearchAdmin() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/research").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, year: parseInt(form.year) || null };
    try {
      if (editId) await api.put(`/admin/research/${editId}`, payload);
      else await api.post("/admin/research", payload);
      toast.success("Saved");
      setForm(empty); setEditId(null); load();
    } catch (err) { toast.error("Save failed"); }
  };

  const edit = (r) => { setForm({ ...r, year: r.year || "" }); setEditId(r.id); };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/research/${id}`);
    toast.success("Deleted"); load();
  };
  const cancel = () => { setForm(empty); setEditId(null); };

  return (
    <div data-testid="admin-research" className="space-y-8">
      <header><div className="label-eyebrow mb-2">Manage</div>
        <h1 className="font-display text-4xl text-[hsl(var(--primary))]">Research Papers</h1></header>

      <form onSubmit={save} className="bg-white border border-[hsl(var(--border-c))] p-6 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Label>Title *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="rp-title" /></div>
        <div><Label>Authors</Label><Input value={form.authors} onChange={(e) => setForm({ ...form, authors: e.target.value })} /></div>
        <div><Label>Journal</Label><Input value={form.journal} onChange={(e) => setForm({ ...form, journal: e.target.value })} /></div>
        <div><Label>Year</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
        <div><Label>URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></div>
        <div className="sm:col-span-2"><Label>Summary</Label><Textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></div>
        <div className="sm:col-span-2 flex gap-3">
          <Button type="submit" data-testid="rp-save" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] rounded-none uppercase tracking-widest">
            {editId ? <Edit size={14} className="mr-2" /> : <Plus size={14} className="mr-2" />}
            {editId ? "Update" : "Add Paper"}
          </Button>
          {editId && <Button type="button" variant="outline" onClick={cancel} className="rounded-none"><X size={14} className="mr-2" />Cancel</Button>}
        </div>
      </form>

      <div className="bg-white border border-[hsl(var(--border-c))]">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-muted))]"><tr>
            {["Title", "Authors", "Year", ""].map((h) => <th key={h} className="text-left p-3 font-medium uppercase text-[10px] tracking-widest">{h}</th>)}
          </tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} className="border-t border-[hsl(var(--border-c))]">
                <td className="p-3 font-medium">{r.title}</td>
                <td className="p-3">{r.authors}</td>
                <td className="p-3">{r.year}</td>
                <td className="p-3 flex gap-3">
                  <button onClick={() => edit(r)} data-testid={`rp-edit-${r.id}`}><Edit size={14} /></button>
                  <button onClick={() => remove(r.id)} data-testid={`rp-del-${r.id}`} className="text-[hsl(var(--accent))]"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
